import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plots, errorMessage } from '../api/client';
import { Plot, PlotMember, PlotRef, PlotSnapshot, PlotTab } from '../types';
import { Icon } from './ui/Icon';
import PlotMembersTab from './plot/PlotMembersTab';
import PlotFlagsTab from './plot/PlotFlagsTab';
import PlotSnapshotsTab from './plot/PlotSnapshotsTab';
import { timeAgo } from '../utils/format';
import { isMergedPlot, plotFootprint, plotUrl, sizeLabel } from '../utils/plots';

interface Props {
  plotRef: PlotRef;
  plot: Plot;
}

const TABS: { id: PlotTab; label: string }[] = [
  { id: 'members', label: 'Players' },
  { id: 'flags', label: 'Flags' },
  { id: 'snapshots', label: 'Snapshots' },
];

const TAB_ALIASES: Record<string, PlotTab> = {
  members: 'members',
  players: 'members',
  trusted: 'members',
  flags: 'flags',
  snapshots: 'snapshots',
  backups: 'snapshots',
};

/** One plot: hero with its facts, then the trusted / added players, flags and snapshots tabs. */
const PlotPage: React.FC<Props> = ({ plotRef, plot }) => {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const resolved = tabParam ? TAB_ALIASES[tabParam.toLowerCase()] : undefined;
  const tab: PlotTab = resolved || 'members';

  // Normalise the URL (missing / legacy tab → canonical).
  useEffect(() => {
    if (tabParam !== tab) navigate(plotUrl(plotRef, tab), { replace: true });
  }, [tabParam, tab, plotRef, navigate]);

  // Players and snapshots are loaded here so the hero can summarise them and the tabs can reuse them.
  const [trusted, setTrusted] = useState<PlotMember[] | null>(null);
  const [members, setMembers] = useState<PlotMember[] | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<PlotSnapshot[] | null>(null);
  const [snapshotsError, setSnapshotsError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadMembers = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([Plots.members(plotRef, 'trusted'), Plots.members(plotRef, 'members')]);
      setTrusted(t);
      setMembers(m);
      setMembersError(null);
    } catch (e) {
      setMembersError(errorMessage(e, "Failed to load the plot's players."));
    }
  }, [plotRef]);

  const loadSnapshots = useCallback(async () => {
    try {
      setSnapshots(await Plots.snapshots(plotRef));
      setSnapshotsError(null);
    } catch (e) {
      setSnapshotsError(errorMessage(e, 'Failed to load snapshots.'));
    }
  }, [plotRef]);

  useEffect(() => {
    loadMembers();
    loadSnapshots();
  }, [loadMembers, loadSnapshots]);

  // Background refresh when the tab regains focus.
  useEffect(() => {
    const onFocus = () => {
      loadMembers();
      loadSnapshots();
      setRefreshKey((k) => k + 1);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadMembers, loadSnapshots]);

  const latestSnapshot = useMemo(
    () => (snapshots || []).reduce<PlotSnapshot | null>((acc, s) => (!acc || s.createdAt > acc.createdAt ? s : acc), null),
    [snapshots]
  );
  const footprint = plotFootprint(plot);
  const merged = isMergedPlot(plot);

  return (
    <div className="main2">
      <header className="hero">
        <div className="hero-in">
          <div className="hero-top">
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => navigate('/plots')}>
              <Icon name="chevron" size={13} style={{ transform: 'rotate(180deg)' }} /> Your plots
            </button>
          </div>
          <div className="hero-id">
            <div style={{ minWidth: 0 }}>
              <h1 className="hero-name">Plot {plot.plotId}</h1>
              <div className="hero-meta">
                <span className="pill">
                  <Icon name="block" size={12} />
                  {sizeLabel(plotRef.size)}
                </span>
                <span className="hero-fact mono">{plot.world}</span>
                {footprint && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">
                      <b>
                        {footprint[0]} × {footprint[1]}
                      </b>{' '}
                      blocks
                    </span>
                  </>
                )}
                {merged && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">merged · {plot.regions.length} regions</span>
                  </>
                )}
                {trusted && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">
                      <b>{trusted.length}</b> trusted
                    </span>
                  </>
                )}
                {members && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">
                      <b>{members.length}</b> added
                    </span>
                  </>
                )}
                {latestSnapshot && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">snapshot {timeAgo(latestSnapshot.createdAt)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <nav className="wnav">
        <div className="wnav-in" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} type="button" className="wtab" role="tab" aria-selected={tab === t.id} onClick={() => navigate(plotUrl(plotRef, t.id))}>
              {t.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="page">
        {tab === 'members' && (
          <PlotMembersTab
            plotRef={plotRef}
            plot={plot}
            trusted={trusted}
            members={members}
            error={membersError}
            onTrustedChange={setTrusted}
            onMembersChange={setMembers}
            onReload={loadMembers}
          />
        )}
        {tab === 'flags' && <PlotFlagsTab plotRef={plotRef} plot={plot} refreshKey={refreshKey} />}
        {tab === 'snapshots' && <PlotSnapshotsTab plotRef={plotRef} plot={plot} snapshots={snapshots} error={snapshotsError} onChanged={loadSnapshots} />}
      </div>
    </div>
  );
};

export default PlotPage;
